import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import AppTheme from "../../styles/AppTheme";
import CssBaseline from "@mui/material/CssBaseline";
import { brown, yellow } from "../../styles/ThemePrimitives";

const apiRoot = () => process.env.REACT_APP_API_BASE || "https://api.fmb52.com/api";
const EMPTY_FILTERS = {
  search: "",
  unpaid: false,
  scheduled_date: "",
};
const EMPTY_SCHEDULE_FILTERS = {
  search: "",
  status: "",
  payment_status: "",
};
const EMPTY_OVERALL_STATS = {
  total_confirmed: 0,
  total_wakalat: 0,
  total_paid: 0,
  total_unpaid: 0,
};

function normalizeListResponse(json) {
  if (Array.isArray(json)) {
    return { rows: json, total: json.length, page: null, perPage: null, overallStats: EMPTY_OVERALL_STATS };
  }

  if (Array.isArray(json?.data)) {
    const pagination = json?.pagination || json?.meta || {};
    const total = Number(pagination?.total ?? json?.total ?? json.data.length) || 0;
    const page = Number(pagination?.current_page ?? json?.current_page) || null;
    const perPage = Number(pagination?.per_page ?? json?.per_page) || null;
    return {
      rows: json.data,
      total,
      page,
      perPage,
      overallStats: {
        total_confirmed: Number(json?.overall_stats?.total_confirmed ?? 0),
        total_wakalat: Number(json?.overall_stats?.total_wakalat ?? 0),
        total_paid: Number(json?.overall_stats?.total_paid ?? 0),
        total_unpaid: Number(json?.overall_stats?.total_unpaid ?? 0),
      },
    };
  }

  const root = json?.data ?? json;
  const inner = root?.data;
  const rows = Array.isArray(inner) ? inner : Array.isArray(root) ? root : [];
  const total = Number(root?.total ?? json?.total ?? rows.length) || 0;
  const hasPageMeta = root && typeof root.current_page === "number";
  const page = hasPageMeta ? Number(root.current_page) || 1 : null;
  const perPage =
    (root && typeof root.per_page === "number" ? Number(root.per_page) : null) ||
    (typeof json?.per_page === "number" ? Number(json.per_page) : null);
  return {
    rows,
    total,
    page,
    perPage,
    overallStats: {
      total_confirmed: Number(root?.overall_stats?.total_confirmed ?? json?.overall_stats?.total_confirmed ?? 0),
      total_wakalat: Number(root?.overall_stats?.total_wakalat ?? json?.overall_stats?.total_wakalat ?? 0),
      total_paid: Number(root?.overall_stats?.total_paid ?? json?.overall_stats?.total_paid ?? 0),
      total_unpaid: Number(root?.overall_stats?.total_unpaid ?? json?.overall_stats?.total_unpaid ?? 0),
    },
  };
}

function formatValidationErrors(payload) {
  const errs = payload?.errors;
  if (errs && typeof errs === "object") {
    return Object.entries(errs)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return payload?.message || "Request failed.";
}

/** Display API `YYYY-MM-DD` (or ISO prefix) as dd-mm-yyyy without timezone shifts. */
function formatScheduledDateForTable(value) {
  if (value == null || value === "") return "—";
  const s = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (dmy) {
    const dd = dmy[1].padStart(2, "0");
    const mm = dmy[2].padStart(2, "0");
    return `${dd}-${mm}-${dmy[3]}`;
  }
  return s;
}

function buildVisibleStats(row) {
  const totals = row?.schedule_totals || {};
  const completedPayment = totals?.completed_payment || {};
  const firstRow = [
    {
      label: "Confirmed",
      value: Number(totals.confirmed ?? 0),
      sx: { backgroundColor: "#e8f5e9", border: "1px solid #c8e6c9", color: "#1b5e20" },
      filters: { status: "confirmed" },
    },
    {
      label: "Wakalat",
      value: Number(totals.wakalat ?? 0),
      sx: { backgroundColor: "#e3f2fd", border: "1px solid #bbdefb", color: "#0d47a1" },
      filters: { status: "wakalat" },
    },
    {
      label: "Schedule Later",
      value: Number(totals.schedule_later ?? 0),
      sx: { backgroundColor: "#ffebee", border: "1px solid #ffcdd2", color: "#b71c1c" },
      filters: { status: "schedule_later" },
    },
  ].map((item) => ({
    ...item,
    value: Number.isFinite(item.value) ? item.value : 0,
  }));

  const secondRow = [
    {
      label: "Paid",
      value: Number(completedPayment.paid ?? 0),
      sx: { backgroundColor: "#e8f5e9", border: "1px solid #c8e6c9", color: "#1b5e20" },
      filters: { payment_status: "1" },
    },
    {
      label: "Pending",
      value: Number(completedPayment.pending ?? 0),
      sx: { backgroundColor: "#fff8e1", border: "1px solid #ffecb3", color: "#8d6e00" },
      filters: { payment_status: "0" },
    },
  ].map((item) => ({
    ...item,
    value: Number.isFinite(item.value) ? item.value : 0,
  }));

  return { firstRow, secondRow };
}

function formatStatusLabel(value) {
  const normalized = String(value || "").toLowerCase();
  if (!normalized) return "—";
  if (normalized === "schedule_later") return "Schedule Later";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatPaymentStatusLabel(value) {
  if (value === 1 || value === "1" || value === true || String(value).toLowerCase() === "paid") {
    return "Paid";
  }
  if (value === 0 || value === "0" || value == null || value === false) {
    return "Pending";
  }
  return String(value);
}

/**
 * @param {{ embedded?: boolean, year?: string }} [props]
 */
export default function ZabihatDates({ embedded, year: yearProp }) {
  const navigate = useNavigate();
  const { token, loading: userLoading } = useUser();
  const outlet = useOutletContext() || {};
  const selectedYear = outlet.selectedYear;

  const yearFromOutlet = useMemo(() => {
    if (selectedYear == null) return "";
    if (Array.isArray(selectedYear)) return selectedYear[0] || "";
    return String(selectedYear);
  }, [selectedYear]);

  const yearFilter = yearProp ?? yearFromOutlet;
  const perPage = 20;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [overallStats, setOverallStats] = useState(EMPTY_OVERALL_STATS);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [dateOptions, setDateOptions] = useState([]);
  const [dateOptionsLoading, setDateOptionsLoading] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);
  const [scheduleFilters, setScheduleFilters] = useState(EMPTY_SCHEDULE_FILTERS);
  const [scheduleRows, setScheduleRows] = useState([]);
  const [scheduleTotal, setScheduleTotal] = useState(0);
  const [schedulePage, setSchedulePage] = useState(0);
  const [schedulePerPage, setSchedulePerPage] = useState(50);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleStatusUpdatingId, setScheduleStatusUpdatingId] = useState(null);
  const [editingCountRowId, setEditingCountRowId] = useState(null);
  const [editingCountValue, setEditingCountValue] = useState("");
  const [countUpdatingId, setCountUpdatingId] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedReceiptRow, setSelectedReceiptRow] = useState(null);
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [receiptDate, setReceiptDate] = useState("");
  const [receiptMode, setReceiptMode] = useState("cash");
  const [receiptAmount, setReceiptAmount] = useState("");
  const [receiptName, setReceiptName] = useState("");
  const [receiptBankName, setReceiptBankName] = useState("");
  const [receiptChequeNo, setReceiptChequeNo] = useState("");
  const [receiptChequeDate, setReceiptChequeDate] = useState("");
  const [receiptTransactionId, setReceiptTransactionId] = useState("");
  const [receiptTransactionDate, setReceiptTransactionDate] = useState("");
  const [scheduleAddDialogOpen, setScheduleAddDialogOpen] = useState(false);
  const [scheduleAddSubmitting, setScheduleAddSubmitting] = useState(false);
  const [scheduleAddError, setScheduleAddError] = useState(null);
  const [scheduleAddUser, setScheduleAddUser] = useState(null);
  const [scheduleAddStatus, setScheduleAddStatus] = useState("confirmed");
  const [scheduleAddCount, setScheduleAddCount] = useState("1");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  /** `null` = add new; number = edit existing id */
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [formYear, setFormYear] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [zabihatCount, setZabihatCount] = useState("0");
  const [amZabihat, setAmZabihat] = useState("0");
  const loadMoreRef = useRef(null);
  const pendingLoadRef = useRef(false);

  const openAddDialog = () => {
    setFormError(null);
    setEditingId(null);
    setFormYear(yearFilter || "1446-1447");
    setScheduledDate("");
    setScheduledTime("");
    setZabihatCount("0");
    setAmZabihat("0");
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setFormError(null);
    setEditingId(row.id);
    setFormYear(row.year ?? "");
    setScheduledDate(row.scheduled_date ? String(row.scheduled_date).slice(0, 10) : "");
    setScheduledTime(row.scheduled_time != null ? String(row.scheduled_time) : "");
    setZabihatCount(String(row.zabihat_count ?? 0));
    setAmZabihat(String(row.am_zabihat ?? 0));
    setDialogOpen(true);
  };

  const closeFormDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const resetFormAfterSuccess = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const openSchedulesDialog = (row, overrides = {}) => {
    setSelectedScheduleDate(row);
    setScheduleFilters({
      ...EMPTY_SCHEDULE_FILTERS,
      ...overrides,
      search: overrides.search ?? "",
      status: overrides.status ?? "",
      payment_status:
        overrides.payment_status != null ? String(overrides.payment_status) : "",
    });
    setSchedulePage(0);
    setScheduleDialogOpen(true);
  };

  const resetScheduleAddForm = () => {
    setScheduleAddUser(null);
    setScheduleAddStatus("confirmed");
    setScheduleAddCount("1");
    setScheduleAddError(null);
  };

  const closeScheduleAddDialog = () => {
    if (scheduleAddSubmitting) return;
    setScheduleAddDialogOpen(false);
    resetScheduleAddForm();
  };

  const resetScheduleAddAfterSuccess = () => {
    setScheduleAddDialogOpen(false);
    resetScheduleAddForm();
  };

  const openReceiptDialog = (row) => {
    setSelectedReceiptRow(row);
    setReceiptError(null);
    setReceiptDate(new Date().toISOString().slice(0, 10));
    setReceiptMode("cash");
    setReceiptAmount(String(Number(row?.count ?? 0) * 6000));
    setReceiptName(row?.name || "");
    setReceiptBankName("");
    setReceiptChequeNo("");
    setReceiptChequeDate("");
    setReceiptTransactionId("");
    setReceiptTransactionDate("");
    setReceiptDialogOpen(true);
  };

  const closeReceiptDialog = () => {
    if (receiptSubmitting) return;
    setReceiptDialogOpen(false);
    setSelectedReceiptRow(null);
    setReceiptError(null);
  };

  const resetReceiptDialogAfterSuccess = () => {
    setReceiptDialogOpen(false);
    setSelectedReceiptRow(null);
    setReceiptError(null);
  };

  const fetchList = useCallback(async (pageToLoad, replace = false) => {
    if (!token || userLoading) return;
    setListLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams();
      if (yearFilter) params.set("year", yearFilter);
      if (appliedFilters.search.trim()) params.set("search", appliedFilters.search.trim());
      if (appliedFilters.unpaid) params.set("unpaid", "1");
      if (appliedFilters.scheduled_date) params.set("scheduled_date", appliedFilters.scheduled_date);
      params.set("page", String(pageToLoad));
      params.set("per_page", String(perPage));
      const url = `${apiRoot()}/zabihat/dates?${params.toString()}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setListError(formatValidationErrors(json));
        setRows([]);
        setTotal(0);
        setOverallStats(EMPTY_OVERALL_STATS);
        return;
      }
      const {
        rows: nextRows,
        total: nextTotal,
        page: apiPage,
        perPage: apiPerPage,
        overallStats: nextOverallStats,
      } = normalizeListResponse(json);
      const effectivePage = apiPage ?? pageToLoad;
      const effectivePerPage = apiPerPage ?? perPage;
      setOverallStats(nextOverallStats || EMPTY_OVERALL_STATS);
      setRows((prev) => {
        if (replace || effectivePage <= 1) return nextRows;
        const seen = new Set(prev.map((item) => item.id));
        return [...prev, ...nextRows.filter((item) => !seen.has(item.id))];
      });
      setTotal(nextTotal);
      if (nextRows.length === 0) {
        setHasMore(false);
      } else if (nextTotal > 0) {
        setHasMore(effectivePage * effectivePerPage < nextTotal);
      } else {
        setHasMore(nextRows.length >= effectivePerPage);
      }
    } catch (e) {
      setListError(e?.message || "Could not load dates.");
      if (replace) {
        setRows([]);
        setTotal(0);
      }
      setOverallStats(EMPTY_OVERALL_STATS);
      setHasMore(false);
    } finally {
      pendingLoadRef.current = false;
      setListLoading(false);
    }
  }, [token, userLoading, yearFilter, appliedFilters, perPage, navigate]);

  useEffect(() => {
    setRows([]);
    setTotal(0);
    setOverallStats(EMPTY_OVERALL_STATS);
    setHasMore(true);
    setPage(1);
    pendingLoadRef.current = false;
    fetchList(1, true);
  }, [fetchList]);

  useEffect(() => {
    if (page <= 1) return;
    fetchList(page, false);
  }, [page, fetchList]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (!firstEntry?.isIntersecting) return;
        if (listLoading || pendingLoadRef.current || !hasMore) return;
        pendingLoadRef.current = true;
        setPage((prev) => prev + 1);
      },
      { root: null, rootMargin: "240px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, listLoading]);

  useEffect(() => {
    document.title = embedded ? "Dashboard - FMB 52" : "Zabihat dates - FMB 52";
  }, [embedded]);

  const fetchDateOptions = useCallback(async () => {
    if (!token || userLoading) return;
    setDateOptionsLoading(true);
    try {
      const params = new URLSearchParams();
      if (yearFilter) params.set("year", yearFilter);
      params.set("limit", "500");
      const res = await fetch(`${apiRoot()}/zabihat/dates/dropdown?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDateOptions([]);
        return;
      }
      setDateOptions(Array.isArray(json?.data) ? json.data : []);
    } catch (_e) {
      setDateOptions([]);
    } finally {
      setDateOptionsLoading(false);
    }
  }, [token, userLoading, yearFilter, navigate]);

  useEffect(() => {
    fetchDateOptions();
  }, [fetchDateOptions]);

  const fetchUsers = useCallback(async () => {
    if (!token || userLoading) return;
    setUsersLoading(true);
    try {
      const res = await fetch("https://api.fmb52.com/api/all_users", {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (Array.isArray(json)) {
        setUsers(json);
      } else if (Array.isArray(json?.data)) {
        setUsers(json.data);
      } else {
        setUsers([]);
      }
    } catch (_e) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [token, userLoading, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchSchedules = useCallback(async () => {
    if (!token || userLoading || !scheduleDialogOpen || !selectedScheduleDate?.id) return;
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      const payload = {
        zabihat_date_id: selectedScheduleDate.id,
        page: schedulePage + 1,
        per_page: schedulePerPage,
      };
      if (scheduleFilters.search.trim()) payload.search = scheduleFilters.search.trim();
      if (scheduleFilters.status) payload.status = scheduleFilters.status;
      if (scheduleFilters.payment_status !== "") payload.payment_status = Number(scheduleFilters.payment_status);

      const res = await fetch(`${apiRoot()}/zabihat/dates/schedules`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (res.status === 404 || res.status === 422) {
        setScheduleError(formatValidationErrors(json));
        setScheduleRows([]);
        setScheduleTotal(0);
        return;
      }
      if (!res.ok) {
        setScheduleError(formatValidationErrors(json));
        setScheduleRows([]);
        setScheduleTotal(0);
        return;
      }
      const { rows: nextRows, total: nextTotal, page: apiPage, perPage: apiPerPage } =
        normalizeListResponse(json);
      setScheduleRows(nextRows);
      setScheduleTotal(nextTotal);
      if (apiPage != null) setSchedulePage(Math.max(0, apiPage - 1));
      if (apiPerPage != null && apiPerPage > 0 && apiPerPage !== schedulePerPage) {
        setSchedulePerPage(apiPerPage);
      }
    } catch (e) {
      setScheduleError(e?.message || "Could not load schedules.");
      setScheduleRows([]);
      setScheduleTotal(0);
    } finally {
      setScheduleLoading(false);
    }
  }, [token, userLoading, scheduleDialogOpen, selectedScheduleDate, scheduleFilters, schedulePage, schedulePerPage, navigate]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleScheduleStatusUpdate = async (row, nextStatus) => {
    if (!row?.id || !nextStatus || nextStatus === row.status) return;
    setScheduleStatusUpdatingId(row.id);
    try {
      const res = await fetch(`${apiRoot()}/zabihat/schedule/${row.id}/status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setScheduleError(formatValidationErrors(json));
        return;
      }
      await fetchSchedules();
      await refreshList();
    } catch (e) {
      setScheduleError(e?.message || "Could not update schedule status.");
    } finally {
      setScheduleStatusUpdatingId(null);
    }
  };

  const startCountEdit = (row) => {
    setScheduleError(null);
    setEditingCountRowId(row.id);
    setEditingCountValue(String(row.count ?? 1));
  };

  const cancelCountEdit = () => {
    setEditingCountRowId(null);
    setEditingCountValue("");
  };

  const handleCountUpdate = async (row) => {
    if (!row?.id) return;
    const parsedCount = Number(editingCountValue);
    if (!Number.isFinite(parsedCount) || parsedCount <= 0) {
      setScheduleError("Count must be greater than 0.");
      return;
    }
    setCountUpdatingId(row.id);
    try {
      const res = await fetch(`${apiRoot()}/zabihat/schedule/${row.id}/count`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ count: parsedCount }),
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setScheduleError(formatValidationErrors(json));
        return;
      }
      cancelCountEdit();
      await fetchSchedules();
      await refreshList();
    } catch (e) {
      setScheduleError(e?.message || "Could not update count.");
    } finally {
      setCountUpdatingId(null);
    }
  };

  const handleCreateReceipt = async () => {
    if (!token || !selectedReceiptRow?.id) return;
    if (!receiptDate || !receiptMode || !receiptAmount || !receiptName.trim()) {
      setReceiptError("Date, name, mode, and amount are required.");
      return;
    }
    if (receiptMode === "cheque" && (!receiptBankName.trim() || !receiptChequeNo.trim() || !receiptChequeDate)) {
      setReceiptError("Bank name, cheque number, and cheque date are required for cheque receipts.");
      return;
    }
    if (receiptMode === "neft" && (!receiptTransactionId.trim() || !receiptTransactionDate)) {
      setReceiptError("Transaction ID and transaction date are required for NEFT receipts.");
      return;
    }

    setReceiptSubmitting(true);
    setReceiptError(null);
    try {
      const payload = {
        t_zabihat_schedule_id: selectedReceiptRow.id,
        name: receiptName.trim(),
        date: receiptDate,
        mode: receiptMode === "neft" ? "NEFT" : receiptMode,
        amount: Number(receiptAmount),
      };

      if (receiptMode === "cheque") {
        payload.bank_name = receiptBankName.trim();
        payload.cheque_no = receiptChequeNo.trim();
        payload.cheque_date = receiptChequeDate;
      }

      if (receiptMode === "neft") {
        payload.transaction_id = receiptTransactionId.trim();
        payload.transaction_date = receiptTransactionDate;
      }

      const res = await fetch(`${apiRoot()}/zabihat/schedule/receipt`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setReceiptError(formatValidationErrors(json));
        return;
      }
      resetReceiptDialogAfterSuccess();
      await fetchSchedules();
      await refreshList();
    } catch (e) {
      setReceiptError(e?.message || "Could not create receipt.");
    } finally {
      setReceiptSubmitting(false);
    }
  };

  const buildFormBody = () => ({
    year: formYear.trim(),
    scheduled_date: scheduledDate.trim(),
    scheduled_time: scheduledTime.trim() || undefined,
    zabihat_count: Number(zabihatCount),
    am_zabihat: Number(amZabihat),
  });

  const refreshList = async () => {
    setRows([]);
    setTotal(0);
    setOverallStats(EMPTY_OVERALL_STATS);
    setHasMore(true);
    setPage(1);
    pendingLoadRef.current = false;
    await fetchList(1, true);
  };

  const handleAddSchedule = async () => {
    if (!token || !selectedScheduleDate?.id) return;
    if (
      !scheduleAddUser?.family_id ||
      !scheduleAddUser?.its ||
      !scheduleAddUser?.name ||
      !scheduleAddStatus ||
      !scheduleAddCount
    ) {
      setScheduleAddError("Please select a user and status.");
      return;
    }
    setScheduleAddSubmitting(true);
    setScheduleAddError(null);
    try {
      const payload = {
        zabihat_date_id: selectedScheduleDate.id,
        family_id: String(scheduleAddUser.family_id),
        its: String(scheduleAddUser.its),
        name: scheduleAddUser.name,
        status: scheduleAddStatus,
        count: Number(scheduleAddCount),
      };
      const res = await fetch(`${apiRoot()}/zabihat/schedule/store`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setScheduleAddError(formatValidationErrors(json));
        return;
      }
      resetScheduleAddAfterSuccess();
      await fetchSchedules();
      await refreshList();
    } catch (e) {
      setScheduleAddError(e?.message || "Could not create Zabihat schedule.");
    } finally {
      setScheduleAddSubmitting(false);
    }
  };

  const handleDraftFilterChange = (key, value) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    setAppliedFilters({
      search: draftFilters.search.trim(),
      unpaid: Boolean(draftFilters.unpaid),
      scheduled_date: draftFilters.scheduled_date,
    });
  }, [draftFilters]);

  const handleScheduleFilterChange = (key, value) => {
    setScheduleFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setSchedulePage(0);
  };

  const handleSaveForm = async () => {
    if (!token) return;
    setSubmitting(true);
    setFormError(null);
    const body = buildFormBody();
    const isEdit = editingId != null;
    const url = isEdit ? `${apiRoot()}/zabihat/dates/${editingId}` : `${apiRoot()}/zabihat/dates`;
    try {
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setFormError(json?.message || "Zabihat date not found.");
        return;
      }
      if (res.status === 422) {
        setFormError(formatValidationErrors(json));
        return;
      }
      if (!res.ok) {
        setFormError(formatValidationErrors(json));
        return;
      }
      resetFormAfterSuccess();
      await refreshList();
    } catch (e) {
      setFormError(e?.message || (isEdit ? "Could not update date." : "Could not create date."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!token || !deleteTarget?.id) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${apiRoot()}/zabihat/dates/${deleteTarget.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setDeleteError(json?.message || "Zabihat date not found.");
        return;
      }
      if (!res.ok) {
        setDeleteError(formatValidationErrors(json));
        return;
      }
      setDeleteTarget(null);
      await refreshList();
    } catch (e) {
      setDeleteError(e?.message || "Could not delete date.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const content = (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        px: { xs: 2, sm: 3, md: 4 },
        pt: embedded ? 2 : 3,
        pb: { xs: 3, sm: 4 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: brown[800] }}>
          Zabihat dates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          sx={{ backgroundColor: yellow[700], "&:hover": { backgroundColor: yellow[800] } }}
        >
          Add new
        </Button>
      </Box>

      {yearFilter ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Filtered by year: {yearFilter}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
          mb: 2,
        }}
      >
        {[
          {
            label: "Total Confirmed",
            value: overallStats.total_confirmed,
            sx: { backgroundColor: "#e8f5e9", border: "1px solid #c8e6c9", color: "#1b5e20" },
          },
          {
            label: "Total Wakalat",
            value: overallStats.total_wakalat,
            sx: { backgroundColor: "#e3f2fd", border: "1px solid #bbdefb", color: "#0d47a1" },
          },
          {
            label: "Total Paid",
            value: overallStats.total_paid,
            sx: { backgroundColor: "#e8f5e9", border: "1px solid #c8e6c9", color: "#1b5e20" },
          },
          {
            label: "Total Unpaid",
            value: overallStats.total_unpaid,
            sx: { backgroundColor: "#fff8e1", border: "1px solid #ffecb3", color: "#8d6e00" },
          },
        ].map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              textAlign: "center",
              ...item.sx,
            }}
          >
            <Typography variant="caption" sx={{ display: "block", color: "inherit", opacity: 0.9 }}>
              {item.label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "inherit" }}>
              {item.value ?? 0}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #F4EBD0",
          borderRadius: 2,
          p: 2,
          mb: 2,
          backgroundColor: "#fffdf8",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            label="Search name / ITS"
            value={draftFilters.search}
            onChange={(e) => handleDraftFilterChange("search", e.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 230 } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 260 } }}>
            <InputLabel id="zabihat-date-filter-label">Date</InputLabel>
            <Select
              labelId="zabihat-date-filter-label"
              label="Date"
              value={draftFilters.scheduled_date}
              onChange={(e) => handleDraftFilterChange("scheduled_date", e.target.value)}
              disabled={dateOptionsLoading}
            >
              <MenuItem value="">All dates</MenuItem>
              {dateOptions.map((option) => (
                <MenuItem key={`${option.id}-${option.scheduled_date}`} value={option.scheduled_date || ""}>
                  {option.label || option.scheduled_date || `Date #${option.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={draftFilters.unpaid}
                onChange={(e) => handleDraftFilterChange("unpaid", e.target.checked)}
              />
            }
            label="Unpaid only"
            sx={{ mr: 0 }}
          />
        </Box>
      </Paper>

      {listError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError(null)}>
          {listError}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ border: "1px solid #F4EBD0", borderRadius: 2, overflow: "hidden", p: 2 }}>
        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>No dates found.</Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            {rows.map((row) => {
              const { firstRow: visiblePrimaryStats, secondRow: visiblePaymentStats } = buildVisibleStats(row);
              const summaryItems = [
                {
                  label: "Date",
                  value: formatScheduledDateForTable(row.scheduled_date),
                  isPrimary: true,
                },
                { label: "Year", value: row.year || "—" },
                { label: "Time", value: row.scheduled_time || "—" },
                { label: "Zabihat count", value: row.zabihat_count ?? 0, alignCenter: true },
                { label: "AM zabihat", value: row.am_zabihat ?? 0, alignCenter: true },
              ];

              return (
                <Paper
                  key={row.id}
                  elevation={0}
                  sx={{
                    border: "1px solid #F4EBD0",
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: "#fffdf8",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: { xs: 1.5, sm: 2.5 },
                        flex: 1,
                      }}
                    >
                      {summaryItems.map((item) => (
                        <Box
                          key={item.label}
                          sx={{ minWidth: item.isPrimary ? 150 : 110, textAlign: item.alignCenter ? "center" : "left" }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textAlign: item.alignCenter ? "center" : "left" }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            variant={item.isPrimary ? "h6" : "body1"}
                            sx={{
                              color: item.isPrimary ? brown[800] : "text.primary",
                              fontWeight: item.isPrimary ? 600 : 500,
                              lineHeight: 1.2,
                              textAlign: item.alignCenter ? "center" : "left",
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="View schedules">
                        <IconButton size="small" onClick={() => openSchedulesDialog(row)} aria-label="View schedules">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(row)} aria-label="Edit row">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {visiblePrimaryStats.length > 0 || visiblePaymentStats.length > 0 ? (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      {visiblePrimaryStats.length > 0 ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "repeat(1, minmax(0, 1fr))",
                              sm: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 1.25,
                            mb: visiblePaymentStats.length > 0 ? 1.25 : 0,
                          }}
                        >
                          {visiblePrimaryStats.map((stat) => (
                            <Paper
                              key={stat.label}
                              elevation={0}
                              sx={{
                                p: 1.25,
                                borderRadius: 1.5,
                                textAlign: "center",
                                cursor: "pointer",
                                ...stat.sx,
                              }}
                              onClick={() => openSchedulesDialog(row, stat.filters)}
                            >
                              <Typography
                                variant="caption"
                                sx={{ display: "block", color: "inherit", opacity: 0.9, textAlign: "center" }}
                              >
                                {stat.label}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: "inherit", textAlign: "center" }}
                              >
                                {stat.value}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      ) : null}

                      {visiblePaymentStats.length > 0 ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "repeat(1, minmax(0, 1fr))",
                              sm: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 1.25,
                          }}
                        >
                          {visiblePaymentStats.map((stat) => (
                            <Paper
                              key={stat.label}
                              elevation={0}
                              sx={{
                                p: 1.25,
                                borderRadius: 1.5,
                                textAlign: "center",
                                cursor: "pointer",
                                ...stat.sx,
                              }}
                              onClick={() => openSchedulesDialog(row, stat.filters)}
                            >
                              <Typography
                                variant="caption"
                                sx={{ display: "block", color: "inherit", opacity: 0.9, textAlign: "center" }}
                              >
                                {stat.label}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: "inherit", textAlign: "center" }}
                              >
                                {stat.value}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      ) : null}
                    </>
                  ) : null}
                </Paper>
              );
            })}
          </Box>
        )}

        {rows.length > 0 ? (
          <Box ref={loadMoreRef} sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            {listLoading && hasMore ? <CircularProgress size={28} /> : null}
          </Box>
        ) : null}
      </Paper>

      <Dialog open={dialogOpen} onClose={closeFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId != null ? "Edit Zabihat date" : "Add Zabihat date"}</DialogTitle>
        <DialogContent>
          {formError ? (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          ) : null}
          <TextField
            margin="dense"
            label="Year"
            fullWidth
            required
            value={formYear}
            onChange={(e) => setFormYear(e.target.value)}
            placeholder="e.g. 1447-1448"
            inputProps={{ maxLength: 20 }}
          />
          <TextField
            margin="dense"
            label="Scheduled date"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Scheduled time (optional)"
            fullWidth
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            inputProps={{ maxLength: 20 }}
          />
          <TextField
            margin="dense"
            label="Zabihat count"
            type="number"
            fullWidth
            required
            inputProps={{ min: 0 }}
            value={zabihatCount}
            onChange={(e) => setZabihatCount(e.target.value)}
          />
          <TextField
            margin="dense"
            label="AM zabihat"
            type="number"
            fullWidth
            required
            inputProps={{ min: 0 }}
            value={amZabihat}
            onChange={(e) => setAmZabihat(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {editingId != null ? (
            <Button
              color="error"
              onClick={() => {
                setDeleteError(null);
                setDeleteTarget({
                  id: editingId,
                  scheduled_date: scheduledDate,
                });
                setDialogOpen(false);
              }}
              disabled={submitting}
              sx={{ mr: "auto" }}
            >
              Delete
            </Button>
          ) : null}
          <Button onClick={closeFormDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveForm}
            disabled={submitting || !formYear.trim() || !scheduledDate.trim()}
          >
            {submitting ? <CircularProgress size={22} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteSubmitting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Zabihat date?</DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {deleteError}
            </Alert>
          ) : null}
          {deleteTarget ? (
            <Typography variant="body2" color="text.secondary">
              This will remove the date entry
              {deleteTarget.scheduled_date
                ? ` (${formatScheduledDateForTable(deleteTarget.scheduled_date)})`
                : ""}
              . Linked schedules keep
              their rows; the date link is cleared on the server.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleteSubmitting}>
            {deleteSubmitting ? <CircularProgress size={22} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scheduleDialogOpen}
        onClose={() => !scheduleLoading && setScheduleDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, pr: 4 }}>
            <span>
              Schedules for{" "}
              {selectedScheduleDate ? formatScheduledDateForTable(selectedScheduleDate.scheduled_date) : ""}
            </span>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                resetScheduleAddForm();
                setScheduleAddDialogOpen(true);
              }}
              sx={{ backgroundColor: yellow[700], "&:hover": { backgroundColor: yellow[800] } }}
            >
              Add Zabihat
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
              mb: 2,
              mt: 0.5,
            }}
          >
            <TextField
              size="small"
              label="Search"
              value={scheduleFilters.search}
              onChange={(e) => handleScheduleFilterChange("search", e.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 220 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
              <InputLabel id="schedule-status-label">Status</InputLabel>
              <Select
                labelId="schedule-status-label"
                label="Status"
                value={scheduleFilters.status}
                onChange={(e) => handleScheduleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="invited">Invited</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="wakalat">Wakalat</MenuItem>
                <MenuItem value="schedule_later">Schedule Later</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
              <InputLabel id="schedule-payment-status-label">Payment status</InputLabel>
              <Select
                labelId="schedule-payment-status-label"
                label="Payment status"
                value={scheduleFilters.payment_status}
                onChange={(e) => handleScheduleFilterChange("payment_status", e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Paid</MenuItem>
                <MenuItem value="0">Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {scheduleError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {scheduleError}
            </Alert>
          ) : null}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#faf6ef" }}>
                  <TableCell>ITS</TableCell>
                  <TableCell>Family ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="center">Count</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell align="center">Receipt</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Due</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduleLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : scheduleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      No schedules found.
                    </TableCell>
                  </TableRow>
                ) : (
                  scheduleRows.map((row, index) => (
                    <TableRow key={row.id ?? `${row.its || "row"}-${row.family_id || index}`}>
                      <TableCell>{row.its || "—"}</TableCell>
                      <TableCell>{row.family_id || "—"}</TableCell>
                      <TableCell>{row.name || "—"}</TableCell>
                      <TableCell align="center">
                        {editingCountRowId === row.id ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={editingCountValue}
                              onChange={(e) => setEditingCountValue(e.target.value)}
                              inputProps={{ min: 1, style: { textAlign: "center", width: 56 } }}
                              sx={{ width: 72 }}
                              disabled={countUpdatingId === row.id}
                            />
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleCountUpdate(row)}
                              disabled={countUpdatingId === row.id}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="inherit"
                              onClick={cancelCountEdit}
                              disabled={countUpdatingId === row.id}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                              "& .count-edit-icon": {
                                opacity: 0,
                                transition: "opacity 0.15s ease",
                              },
                              "&:hover .count-edit-icon": {
                                opacity: row.payment_status === true || row.payment_status === 1 || row.payment_status === "1" ? 0 : 1,
                              },
                            }}
                          >
                            <span>{row.count ?? "—"}</span>
                            {row.payment_status === true || row.payment_status === 1 || row.payment_status === "1" ? null : (
                              <IconButton
                                size="small"
                                className="count-edit-icon"
                                onClick={() => startCountEdit(row)}
                                aria-label="Edit count"
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{row.mobile || "—"}</TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={row.status || ""}
                            onChange={(e) => handleScheduleStatusUpdate(row, e.target.value)}
                            disabled={
                              scheduleStatusUpdatingId === row.id ||
                              row.payment_status === true ||
                              row.payment_status === 1 ||
                              row.payment_status === "1"
                            }
                          >
                            <MenuItem value="invited">Invited</MenuItem>
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="wakalat">Wakalat</MenuItem>
                            <MenuItem value="schedule_later">Schedule Later</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>{formatPaymentStatusLabel(row.payment_status)}</TableCell>
                      <TableCell align="center">
                        {(row.status === "confirmed" || row.status === "wakalat") &&
                        !(row.payment_status === true || row.payment_status === 1 || row.payment_status === "1") ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReceiptLongIcon />}
                            onClick={() => openReceiptDialog(row)}
                          >
                            Receipt
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">{row.amount ?? "—"}</TableCell>
                      <TableCell align="right">{row.amount_paid ?? "—"}</TableCell>
                      <TableCell align="right">{row.amount_due ?? "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={scheduleTotal}
            page={schedulePage}
            onPageChange={(_, nextPage) => setSchedulePage(nextPage)}
            rowsPerPage={schedulePerPage}
            onRowsPerPageChange={(e) => {
              setSchedulePerPage(Number(e.target.value) || 50);
              setSchedulePage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200, 500]}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)} disabled={scheduleLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scheduleAddDialogOpen} onClose={closeScheduleAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Zabihat for{" "}
          {selectedScheduleDate ? formatScheduledDateForTable(selectedScheduleDate.scheduled_date) : ""}
        </DialogTitle>
        <DialogContent>
          {scheduleAddError ? (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {scheduleAddError}
            </Alert>
          ) : null}

          <Autocomplete
            options={users}
            loading={usersLoading}
            value={scheduleAddUser}
            onChange={(_, newValue) => {
              setScheduleAddUser(newValue || null);
              setScheduleAddError(null);
            }}
            isOptionEqualToValue={(option, value) => String(option?.family_id ?? "") === String(value?.family_id ?? "")}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") return option;
              const familyId = option.family_id ? ` [${option.family_id}]` : "";
              const its = option.its ? ` (${option.its})` : "";
              return `${option.name || ""}${familyId}${its}`;
            }}
            filterOptions={(options, state) => {
              const search = String(state.inputValue || "").trim().toLowerCase();
              if (!search) return options.slice(0, 100);
              return options
                .filter((option) =>
                  [option.name, option.its, option.family_id]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(search))
                )
                .slice(0, 100);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Select user"
                placeholder="Search by family_id / ITS / name"
                fullWidth
              />
            )}
            sx={{ mt: 1 }}
          />

          {scheduleAddUser ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Family ID: {scheduleAddUser.family_id || "—"} | ITS: {scheduleAddUser.its || "—"}
            </Typography>
          ) : null}

          <FormControl fullWidth margin="dense">
            <InputLabel id="schedule-add-status-label">Status</InputLabel>
            <Select
              labelId="schedule-add-status-label"
              label="Status"
              value={scheduleAddStatus}
              onChange={(e) => setScheduleAddStatus(e.target.value)}
            >
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="wakalat">Wakalat</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Count"
            type="number"
            fullWidth
            required
            inputProps={{ min: 1 }}
            value={scheduleAddCount}
            onChange={(e) => setScheduleAddCount(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeScheduleAddDialog} disabled={scheduleAddSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSchedule}
            disabled={scheduleAddSubmitting || !scheduleAddUser || !scheduleAddCount}
          >
            {scheduleAddSubmitting ? <CircularProgress size={22} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receiptDialogOpen} onClose={closeReceiptDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Receipt</DialogTitle>
        <DialogContent>
          {receiptError ? (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {receiptError}
            </Alert>
          ) : null}

          {selectedReceiptRow ? (
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #F4EBD0", backgroundColor: "#fffdf8" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {selectedReceiptRow.name || "—"}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  ITS: {selectedReceiptRow.its || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Family ID: {selectedReceiptRow.family_id || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {formatStatusLabel(selectedReceiptRow.status)}
                </Typography>
              </Box>
            </Paper>
          ) : null}

          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={receiptName}
            onChange={(e) => setReceiptName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="receipt-mode-label">Mode</InputLabel>
            <Select
              labelId="receipt-mode-label"
              label="Mode"
              value={receiptMode}
              onChange={(e) => setReceiptMode(String(e.target.value))}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="neft">NEFT</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            inputProps={{ min: 0 }}
            value={receiptAmount}
            disabled
            helperText={`Calculated as 6000 x ${selectedReceiptRow?.count ?? 0}`}
          />

          {receiptMode === "cheque" ? (
            <>
              <TextField
                margin="dense"
                label="Bank name"
                fullWidth
                value={receiptBankName}
                onChange={(e) => setReceiptBankName(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Cheque number"
                fullWidth
                value={receiptChequeNo}
                onChange={(e) => setReceiptChequeNo(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Cheque date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={receiptChequeDate}
                onChange={(e) => setReceiptChequeDate(e.target.value)}
              />
            </>
          ) : null}

          {receiptMode === "neft" ? (
            <>
              <TextField
                margin="dense"
                label="Transaction ID"
                fullWidth
                value={receiptTransactionId}
                onChange={(e) => setReceiptTransactionId(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Transaction date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={receiptTransactionDate}
                onChange={(e) => setReceiptTransactionDate(e.target.value)}
              />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReceiptDialog} disabled={receiptSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateReceipt} disabled={receiptSubmitting}>
            {receiptSubmitting ? <CircularProgress size={22} /> : "Save Receipt"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <AppTheme>
      <CssBaseline />
      {content}
    </AppTheme>
  );
}
